import useIsMobile from '../../../utils/mediaQuery';
import { Modal, Image, Upload, message, Button, Input, Tag } from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  CameraOutlined,
  PictureOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import './AssetView.css';
import TextArea from 'antd/es/input/TextArea';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  usePostAssetMutation,
  usePutAssetMutation,
  useGetAssetPhotoUploadUrlMutation,
  useDeleteAssetPhotoMutation,
} from '../../../features/api/apiSlice';
import { createPhotoObject, getPhotoDisplayUrl } from '../../../utils/photoUtilities';
import LazyPhoto from '../LazyPhoto';
import '../LazyPhoto.css';
import { uploadPhotoViaPresignedUrl, resolveUploadBody } from '../../../services/s3PhotoUpload';
import { Dropdown } from 'primereact/dropdown';
import { Button as PrimeButton } from 'primereact/button';
import useClickOutside from '../../../utils/useClickOutside';
import { v4 as uuidv4 } from 'uuid';

const generateDraftKey = () => {
  return uuidv4();
};

const PHOTO_STATUS = {
  UPLOADING: 'uploading',
  UPLOADED: 'uploaded',
  ERROR: 'error',
  DELETING: 'deleting',
};

const STATUS_STYLES = {
  [PHOTO_STATUS.UPLOADING]: { color: 'gold', label: 'Uploading' },
  [PHOTO_STATUS.UPLOADED]: { color: 'green', label: 'Uploaded' },
  [PHOTO_STATUS.ERROR]: { color: 'red', label: 'Error' },
  [PHOTO_STATUS.DELETING]: { color: 'orange', label: 'Removing' },
};

const AssetView = ({
  asset,
  allAssets,
  isOpen,
  handleClose,
  onAssetUpdated,
  ppkPosition,
  ppkLoading,
  locationType,
  messageContextHolder,
  locationTimedOut,
  requestPPKPosition,
}) => {
  const CURRENT_SCREEN = 'ASSET_VIEW';
  const isMobile = useIsMobile();

  const [notes, setNotes] = useState(asset?.note ?? '');
  const [assetName, setAssetName] = useState(asset?.name ?? '');
  const [selectedAsset, setSelectedAsset] = useState(asset);
  const [photosMeta, setPhotosMeta] = useState([]);
  const [removedPhotoIds, setRemovedPhotoIds] = useState(new Set()); // Track removed existing photo IDs
  const [draftKey] = useState(() => generateDraftKey());
  const [loading, setLoading] = useState(false);
  const [updateAsset, { isLoading: isUpdatingAsset }] = usePutAssetMutation();
  const [createAsset, { isLoading: isCreatingAsset }] = usePostAssetMutation();
  const [getAssetPhotoUploadUrl] = useGetAssetPhotoUploadUrlMutation();
  const [deleteAssetPhoto] = useDeleteAssetPhotoMutation();
  const [submitting, setSubmitting] = useState(false);

  const hasInitialized = useRef(false);
  const assetModalRef = useRef(null);
  const photosMetaRef = useRef([]);
  const hasSubmittedRef = useRef(false);
  const enqueuePhotoUploadRef = useRef(null);
  const projectId = asset?.project_id;

  const [isSpinning, setIsSpinning] = useState(false);
  const hasUploadingPhotos = photosMeta.some((photo) => photo.status === PHOTO_STATUS.UPLOADING);

  useEffect(() => {
    photosMetaRef.current = photosMeta;
  }, [photosMeta]);

  const performUpload = useCallback(
    async (tempId, photoData, overrideDraftKey = null) => {
      const activeDraftKey = overrideDraftKey || draftKey;

      if (!activeDraftKey || !projectId) {
        message.error(
          'There was an error: Cannot upload photo. Missing draft key. Please try again.'
        );
        setPhotosMeta((prev) =>
          prev.map((photo) =>
            photo.tempId === tempId
              ? {
                  ...photo,
                  status: PHOTO_STATUS.ERROR,
                  errorMessage: 'Missing draft key',
                }
              : photo
          )
        );
        return;
      }

      try {
        const { body, contentType } = resolveUploadBody(photoData);
        const { id } = await uploadPhotoViaPresignedUrl({
          requestUploadUrl: (payload) => getAssetPhotoUploadUrl({ projectId, payload }).unwrap(),
          body,
          contentType,
          uploadPayload: {
            draft_key: activeDraftKey,
            mime_type: contentType,
            file_name: photoData.fileName || photoData.name || 'Photo',
            file_size: photoData.fileSize || (body.size ?? null),
          },
        });

        setPhotosMeta((prev) =>
          prev.map((photo) =>
            photo.tempId === tempId
              ? {
                  ...photo,
                  id,
                  status: PHOTO_STATUS.UPLOADED,
                  uploadStatus: PHOTO_STATUS.UPLOADED,
                  isExisting: false,
                }
              : photo
          )
        );
      } catch (error) {
        console.error('Failed to upload photo', error);
        message.error(
          error?.data?.error ||
            error?.message ||
            'There was an error uploading the photo, please try again.'
        );
        setPhotosMeta((prev) =>
          prev.map((photo) =>
            photo.tempId === tempId
              ? {
                  ...photo,
                  status: PHOTO_STATUS.ERROR,
                  errorMessage: error?.data?.error || error?.message || 'Failed to upload photo',
                }
              : photo
          )
        );
      }
    },
    [draftKey, projectId, getAssetPhotoUploadUrl]
  );

  const enqueuePhotoUpload = useCallback(
    (photoData, options = {}) => {
      if (!photoData?.file && !photoData?.base64 && !photoData?.url) {
        message.error('Missing photo data.');
        return;
      }

      const tempId = options.tempId || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
      const fileType = photoData.fileType || photoData.mime_type || 'image/jpeg';
      const previewUrl =
        photoData.previewUrl ||
        (photoData.file ? URL.createObjectURL(photoData.file) : null) ||
        (photoData.base64
          ? photoData.base64.startsWith('data:')
            ? photoData.base64
            : `data:${fileType};base64,${photoData.base64}`
          : null);
      const entry = {
        tempId,
        id: photoData.id || null,
        file: photoData.file || null,
        base64: photoData.base64 || null,
        previewUrl,
        url: photoData.url || null,
        fileName: photoData.fileName || photoData.name || 'Photo',
        fileType,
        fileSize: photoData.fileSize || null,
        location: photoData.location || null,
        status: photoData.id ? PHOTO_STATUS.UPLOADED : PHOTO_STATUS.UPLOADING,
        errorMessage: null,
      };

      setPhotosMeta((prev) => {
        if (options.tempId && prev.some((p) => p.tempId === options.tempId)) {
          return prev;
        }
        if (entry.fileName) {
          const isDuplicate = prev.some((p) => p.fileName && p.fileName === entry.fileName);
          if (isDuplicate) {
            return prev;
          }
        }
        return options.prepend ? [entry, ...prev] : [...prev, entry];
      });

      if (!photoData.id) {
        void performUpload(tempId, entry, options.overrideDraftKey);
      }
    },
    [performUpload]
  );

  useEffect(() => {
    enqueuePhotoUploadRef.current = enqueuePhotoUpload;
  }, [enqueuePhotoUpload]);

  const retryPhotoUpload = useCallback(
    (tempId) => {
      const target = photosMetaRef.current.find((photo) => photo.tempId === tempId);
      if (!target) return;

      setPhotosMeta((prev) =>
        prev.map((photo) =>
          photo.tempId === tempId
            ? { ...photo, status: PHOTO_STATUS.UPLOADING, errorMessage: null }
            : photo
        )
      );

      void performUpload(tempId, target);
    },
    [performUpload]
  );

  const handleDeletePhoto = useCallback(
    async (idx) => {
      const photo = photosMetaRef.current[idx];
      if (!photo) return;

      if (photo.status === PHOTO_STATUS.UPLOADING) {
        message.warning('Please wait for the upload to finish before deleting this photo.');
        return;
      }

      if (photo.status === PHOTO_STATUS.DELETING) {
        return;
      }

      // If photo has an ID, delete it from server
      if (photo.id && !photo.isExisting) {
        setPhotosMeta((prev) =>
          prev.map((p, i) => (i === idx ? { ...p, status: PHOTO_STATUS.DELETING } : p))
        );

        try {
          await deleteAssetPhoto({ projectId, photoId: photo.id }).unwrap();
          setPhotosMeta((prev) => prev.filter((_, i) => i !== idx));
          message.success('Photo deleted successfully');
        } catch (error) {
          console.error('Failed to delete photo', error);
          message.error('There was an error deleting the photo, please try again.');
          setPhotosMeta((prev) =>
            prev.map((p, i) =>
              i === idx ? { ...p, status: PHOTO_STATUS.UPLOADED, errorMessage: null } : p
            )
          );
        }
      } else {
        // Just remove from local state (existing photos or not yet uploaded)
        // If it's an existing photo, track its ID so we exclude it from the payload
        if (photo.id && photo.isExisting) {
          setRemovedPhotoIds((prev) => new Set([...prev, photo.id]));
        }
        setPhotosMeta((prev) => prev.filter((_, i) => i !== idx));
        message.success('Photo removed successfully');
      }
    },
    [deleteAssetPhoto, projectId]
  );

  const cleanupDraftUploads = useCallback(
    async (photoList) => {
      const deletable = (photoList || [])
        .filter(
          (photo) =>
            photo.id &&
            (photo.status === PHOTO_STATUS.UPLOADED || photo.status === PHOTO_STATUS.DELETING) &&
            !photo.isExisting &&
            !photo.isSubmitted // Don't delete photos that were submitted/linked to an asset
        )
        .map((photo) => photo.id);

      if (!deletable.length) return;

      await Promise.all(
        deletable.map((photoId) =>
          deleteAssetPhoto({ projectId, photoId })
            .unwrap()
            .catch((error) => {
              console.error('Failed to delete staged photo', photoId, error);
            })
        )
      );
    },
    [deleteAssetPhoto, projectId]
  );

  const refreshPPKPosition = () => {
    requestPPKPosition();
  };

  useEffect(() => {
    if (ppkLoading) {
      setIsSpinning(true);
    } else {
      setIsSpinning(false);
    }
  }, [ppkLoading]);

  useEffect(() => {
    log('-----------------------');
    log('Selected asset:');
    log(selectedAsset);
    log('-----------------------');
  }, [selectedAsset]);

  const closeModal = () => {
    if (!hasSubmittedRef.current) {
      cleanupDraftUploads(photosMetaRef.current);
    }
    setPhotosMeta([]);
    setLoading(false);
    hasInitialized.current = false;
    hasSubmittedRef.current = false;
    handleClose();
  };

  useClickOutside(
    assetModalRef,
    [
      '#asset-modal-wrapper',
      '.p-dropdown-panel',
      '.ant-modal-mask',
      '.ant-image-preview-operations-wrapper',
      '.ant-image-preview-root',
    ],
    closeModal
  );

  // WebView
  const log = (msg) => {
    window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'LOG', content: msg }));
  };

  const openCamera = () => {
    log('Opening camera from: ' + CURRENT_SCREEN);
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'TRIGGER-CAMERA', screen: CURRENT_SCREEN })
    );
  };
  const openGallery = () => {
    log('Opening gallery from: ' + CURRENT_SCREEN);
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'TRIGGER-GALLERY', screen: CURRENT_SCREEN })
    );
  };
  const clearMobilePhotos = useCallback(() => {
    log('Clearing mobile photos from: ' + CURRENT_SCREEN);
    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ type: 'CLEAR-PHOTOS', screen: CURRENT_SCREEN })
    );
  }, []);

  useEffect(() => {
    log('PPK position for location type: ' + JSON.parse(JSON.stringify(locationType)));
    log(JSON.parse(JSON.stringify(ppkPosition)));
  }, [ppkPosition, ppkLoading, locationType]);

  useEffect(() => {
    if (!hasInitialized.current) {
      const existingPhotos =
        asset?.photos
          ?.map((photo, idx) => {
            // Only mark as existing if photo has an actual ID from the database
            // Photos from mobile that haven't been uploaded yet won't have IDs
            const hasId = photo.id != null && photo.id !== undefined;
            const isExisting = hasId;

            const standardPhoto = createPhotoObject({
              ...photo,
              fileName: photo.fileName || `Photo-${idx + 1}`,
              isExisting: isExisting,
            });

            standardPhoto.tempId = hasId
              ? `existing-${photo.id}`
              : `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;

            // If photo has an ID, it's already uploaded. Otherwise, it needs to be uploaded.
            standardPhoto.status = hasId ? PHOTO_STATUS.UPLOADED : PHOTO_STATUS.UPLOADING;

            if (typeof photo === 'string') {
              standardPhoto.url = photo;
            }

            return standardPhoto;
          })
          .filter(Boolean) || [];

      setPhotosMeta(existingPhotos);
      setRemovedPhotoIds(new Set()); // Reset removed photo IDs when initializing
      hasInitialized.current = true;

      // Upload any photos that don't have IDs yet (new photos from mobile)
      // Use setTimeout to ensure enqueuePhotoUploadRef is set
      setTimeout(() => {
        existingPhotos.forEach((photo) => {
          if (
            !photo.id &&
            photo.status === PHOTO_STATUS.UPLOADING &&
            enqueuePhotoUploadRef.current
          ) {
            // Pass tempId to prevent duplication in photosMeta
            enqueuePhotoUploadRef.current(photo, { tempId: photo.tempId });
          }
        });
      }, 0);
    }

    setNotes(asset?.note ?? '');

    return () => {
      if (!hasSubmittedRef.current) {
        cleanupDraftUploads(photosMetaRef.current);
      }
      setPhotosMeta([]);
      setRemovedPhotoIds(new Set()); // Reset removed photo IDs on cleanup
      setLoading(false);
      hasInitialized.current = false;
      hasSubmittedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.id, asset?.isNew, cleanupDraftUploads]);

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must smaller than 2MB!');
      return false;
    }
    return false; // Prevent automatic upload, we'll handle it manually
  };

  const handleChange = (info) => {
    const file = info.file.originFileObj || info.file;

    setLoading(true);

    try {
      enqueuePhotoUpload({
        file,
        previewUrl: URL.createObjectURL(file),
        fileName: file.name,
        fileType: file.type || 'image/jpeg',
        fileSize: file.size,
      });
      message.success('Photo upload started');
    } catch (error) {
      console.error('Failed to process photo:', error);
      message.error('There was an error processing the photo, please try again.');
    } finally {
      setLoading(false);
    }
  };

  const areCoordinatesTheSame = (lat1, lon1, lat2, lon2) => {
    return lat1 === lat2 && lon1 === lon2;
  };

  const buildPhotoIds = () => {
    // Collect photo IDs from photosMeta (newly uploaded photos)
    const photosMetaIds = photosMeta
      .filter((photo) => {
        // Include photos that have an id and are in UPLOADED status
        // This covers:
        // 1. Existing photos (isExisting: true, id: <number>, status: UPLOADED)
        // 2. Newly uploaded photos with draft_key (id: <number>, status: UPLOADED)
        // 3. Newly uploaded photos with asset_id (id: <number>, status: UPLOADED)
        return photo.id && photo.status === PHOTO_STATUS.UPLOADED;
      })
      .map((photo) => photo.id)
      .filter((id) => id != null); // Ensure we only include valid IDs

    // When appending to an existing asset (selectedAsset is set and has an id),
    // we also need to include existing photos from that asset
    // Otherwise, when we update the asset, the API will unlink existing photos
    let selectedAssetPhotoIds = [];
    if (
      selectedAsset &&
      selectedAsset.id &&
      selectedAsset.id !== -1 &&
      Array.isArray(selectedAsset.photos)
    ) {
      // Extract photo IDs from selectedAsset.photos
      // Photos from API are objects with id property (see getAssetsQuery.js)
      // Exclude photos that were removed by the user
      selectedAssetPhotoIds = selectedAsset.photos
        .map((photo) => {
          if (typeof photo === 'object' && photo.id) {
            return Number(photo.id);
          }
          // Handle legacy string format (though API should always return objects)
          return null;
        })
        .filter((id) => id != null && Number.isInteger(id) && !removedPhotoIds.has(id));
    }

    // Combine both sets of photo IDs, removing duplicates
    const allPhotoIds = [...new Set([...selectedAssetPhotoIds, ...photosMetaIds])];
    return allPhotoIds;
  };

  // Get only newly captured photo IDs (not from selected asset)
  // Used when creating a new asset - we don't want to steal photos from existing assets
  const getNewPhotoIds = () => {
    const newPhotoIds = photosMeta
      .filter(
        (photo) => photo.id && photo.status === PHOTO_STATUS.UPLOADED && photo.isExisting !== true // Include photos that are not explicitly marked as existing
      )
      .map((photo) => photo.id)
      .filter((id) => id != null);

    return newPhotoIds;
  };

  const handleModifyExistingAssetFromPlusMenu = () => {
    const ppkLatitude = ppkPosition?.latitude;
    const ppkLongitude = ppkPosition?.longitude;
    const ppkHeight = ppkPosition?.height || ppkPosition?.altitude;

    // Check if selected asset has no coordinates (Case 4: asset without location)
    const selectedAssetHasNoCoordinates =
      (selectedAsset?.latitude === null || selectedAsset?.latitude === undefined) &&
      (selectedAsset?.longitude === null || selectedAsset?.longitude === undefined);

    // Check if coordinates are different - if so, create new asset instead
    const coordinatesChanged =
      !selectedAssetHasNoCoordinates &&
      !areCoordinatesTheSame(
        ppkLatitude,
        ppkLongitude,
        selectedAsset?.latitude,
        selectedAsset?.longitude
      );

    if (coordinatesChanged) {
      log('Coordinates changed - creating new asset instead of updating');

      // When creating a NEW asset, only use newly captured photos
      // Don't include photos from the selected asset - they belong to that asset
      const newPhotoIds = getNewPhotoIds();

      const newAssetPayload = {
        name: selectedAsset?.name,
        drawing_id: selectedAsset?.drawing_id,
        depth: selectedAsset?.depth || 0,
        altitude: ppkHeight || 0,
        note: notes,
        geolocation: [ppkLongitude, ppkLatitude],
        photo_ids: Array.isArray(newPhotoIds) ? newPhotoIds : [newPhotoIds],
      };

      hasSubmittedRef.current = true;
      // Mark all photos as submitted so they won't be deleted by cleanup
      setPhotosMeta((prev) =>
        prev.map((photo) => ({
          ...photo,
          isSubmitted: true,
        }))
      );
      createAsset({
        projectId: asset?.project_id,
        asset: newAssetPayload,
      })
        .then((res) => {
          if (res.error) {
            hasSubmittedRef.current = false;
            message.error('Failed to create new asset');
            clearMobilePhotos();
          } else {
            message.success('New asset created successfully!');
            onAssetUpdated({ ...res.data, ...newAssetPayload });
            clearMobilePhotos();
            closeModal();
          }
        })
        .catch((err) => {
          hasSubmittedRef.current = false;
          message.error('Failed to create new asset');
          clearMobilePhotos();
        });
    } else {
      // Case 4: Selected asset has no coordinates - update with new location
      // OR: Coordinates are the same - update existing asset

      const photosWithErrors = photosMeta.filter((photo) => photo.status === PHOTO_STATUS.ERROR);
      if (photosWithErrors.length > 0) {
        message.warning(
          `There are ${photosWithErrors.length} photo(s) with errors. They will not be included in the asset.`
        );
      }

      const photosStillUploading = photosMeta.filter(
        (photo) => photo.status === PHOTO_STATUS.UPLOADING
      );
      if (photosStillUploading.length > 0) {
        message.warning(
          `There are ${photosStillUploading.length} photo(s) still uploading. Please wait for them to finish.`
        );
        return;
      }

      // When updating existing asset, include both existing photos and newly uploaded photos
      const photoIds = buildPhotoIds();

      const payload = {
        note: notes,
        photo_ids: Array.isArray(photoIds) ? photoIds : [photoIds],
      };

      // If selected asset has no coordinates, update with new location
      if (selectedAssetHasNoCoordinates && ppkLatitude && ppkLongitude) {
        log('Selected asset has no coordinates - updating with new location');
        payload.latitude = ppkLatitude;
        payload.longitude = ppkLongitude;
        if (ppkHeight !== null && ppkHeight !== undefined) {
          payload.altitude = ppkHeight;
        }
      }

      hasSubmittedRef.current = true;
      // Mark all photos as submitted so they won't be deleted by cleanup
      setPhotosMeta((prev) =>
        prev.map((photo) => ({
          ...photo,
          isSubmitted: true,
        }))
      );
      updateAsset({
        projectId: asset?.project_id,
        assetId: selectedAsset?.id,
        ...payload,
      })
        .then((res) => {
          if (res.error) {
            hasSubmittedRef.current = false;
            message.error('Failed to save changes');
            clearMobilePhotos();
          } else {
            message.success(
              selectedAssetHasNoCoordinates
                ? 'Asset updated with new location!'
                : 'Changes saved successfully!'
            );
            onAssetUpdated({ ...res.data, ...payload });
            clearMobilePhotos();
            closeModal();
          }
        })
        .catch((err) => {
          hasSubmittedRef.current = false;
          message.error('Failed to save changes');
          clearMobilePhotos();
        });
    }
  };

  const handleCreateNewAsset = () => {
    const photosWithErrors = photosMeta.filter((photo) => photo.status === PHOTO_STATUS.ERROR);
    if (photosWithErrors.length > 0) {
      message.warning(
        `There are ${photosWithErrors.length} photo(s) with errors. They will not be included in the asset.`
      );
    }

    const photosStillUploading = photosMeta.filter(
      (photo) => photo.status === PHOTO_STATUS.UPLOADING
    );
    if (photosStillUploading.length > 0) {
      message.warning(
        `There are ${photosStillUploading.length} photo(s) still uploading. Please wait for them to finish.`
      );
      return;
    }

    // When creating a new asset, only use newly uploaded photos (not from selectedAsset)
    const photoIds = getNewPhotoIds();

    const ppkLatitude = ppkPosition?.latitude;
    const ppkLongitude = ppkPosition?.longitude;
    const ppkHeight = ppkPosition?.height || ppkPosition?.altitude;

    const payload = {
      name: assetName,
      note: notes,
      photo_ids: Array.isArray(photoIds) && photoIds.length > 0 ? photoIds : [],
      geolocation: [ppkLongitude, ppkLatitude],
      altitude: ppkHeight || 0,
      depth: 0,
      installed_at: new Date().toISOString(),
    };

    hasSubmittedRef.current = true;
    // Mark all photos as submitted so they won't be deleted by cleanup
    setPhotosMeta((prev) =>
      prev.map((photo) => ({
        ...photo,
        isSubmitted: true, // Mark as submitted to prevent cleanup deletion
      }))
    );
    createAsset({
      projectId: asset?.project_id,
      asset: payload,
    })
      .then((res) => {
        if (res.error) {
          hasSubmittedRef.current = false;
          // Unmark photos if submission failed
          setPhotosMeta((prev) =>
            prev.map((photo) => ({
              ...photo,
              isSubmitted: false,
            }))
          );
          message.error('Failed to create asset');
          clearMobilePhotos();
        } else {
          message.success('Asset created successfully!');

          onAssetUpdated({
            ...res.data,
            ...payload,
          });
          clearMobilePhotos();
          closeModal();
        }
      })
      .catch((err) => {
        hasSubmittedRef.current = false;
        message.error('There was an error creating the asset, please try again.');
        clearMobilePhotos();
      });
  };

  const handleModifyExistingAssetFromAssetList = () => {
    const photoIds = buildPhotoIds();

    const payload = {
      note: notes,
      photo_ids: Array.isArray(photoIds) ? photoIds : [photoIds],
    };

    hasSubmittedRef.current = true;
    // Mark all photos as submitted so they won't be deleted by cleanup
    setPhotosMeta((prev) =>
      prev.map((photo) => ({
        ...photo,
        isSubmitted: true,
      }))
    );
    updateAsset({
      projectId: asset?.project_id,
      assetId: asset?.id,
      ...payload,
    })
      .then((res) => {
        if (res.error) {
          hasSubmittedRef.current = false;
          message.error('Failed to save changes');
          clearMobilePhotos();
        } else {
          message.success('Changes saved successfully!');
          onAssetUpdated({ ...res.data, ...payload });
          clearMobilePhotos();
          closeModal();
        }
      })
      .catch((err) => {
        hasSubmittedRef.current = false;
        message.error('There was an error saving changes, please try again.');
        clearMobilePhotos();
      });
  };

  const handleSave = () => {
    const isCreatingNewAsset =
      (selectedAsset === null || selectedAsset?.value === null) && asset?.isNew;
    const isModifyingExistingAssetFromPlusMenu = selectedAsset && selectedAsset.id && asset?.isNew;
    const isModifyingExistingAssetFromAssetList = !asset?.isNew;

    setSubmitting(true);

    // Case 1: Open directly from plus menu and user selected to modify an existing asset
    if (isModifyingExistingAssetFromPlusMenu) {
      handleModifyExistingAssetFromPlusMenu(ppkPosition);
    } else if (isCreatingNewAsset) {
      handleCreateNewAsset(ppkPosition);
    } else if (isModifyingExistingAssetFromAssetList) {
      handleModifyExistingAssetFromAssetList(ppkPosition);
    } else {
      message.error('Failed to save changes, please try again');
    }

    setSubmitting(false);
  };

  const handleWebViewMessage = useCallback(
    async (event) => {
      let msg = event.data;
      if (typeof msg === 'string') {
        const trimmed = msg.trim();
        if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
          try {
            msg = JSON.parse(trimmed);
          } catch (e) {
            throw new Error('Invalid message from WebView, could not parse JSON: ' + trimmed, e);
          }
        } else return;
      }

      if (msg.type === 'PHOTOS_UPDATE' && msg.screen === CURRENT_SCREEN) {
        const data = msg;

        msg.photos.forEach((photo, idx) => {
          if (!photo.base64 && !photo.file) {
            console.error('Photo from mobile missing image data:', photo);
            message.error('There was an error: Photo is missing image data. Please try again.');
            return;
          }

          const photoFileName = photo.fileName || photo.name;
          if (photoFileName) {
            const isDuplicate = photosMetaRef.current.some((existingPhoto) => {
              return existingPhoto.fileName && existingPhoto.fileName === photoFileName;
            });

            if (isDuplicate) return;
          }

          enqueuePhotoUpload({
            ...createPhotoObject({ ...photo, isExisting: false }),
            base64: photo.base64 || null,
            fileName: photo.fileName || photo.name || 'Photo',
            fileType: photo.fileType || photo.mime_type || 'image/jpeg',
            location: photo.location || null,
          });
        });

        clearMobilePhotos();
      }
    },
    [enqueuePhotoUpload, clearMobilePhotos, selectedAsset, asset]
  );

  useEffect(() => {
    window.addEventListener('message', handleWebViewMessage);
    document.addEventListener('message', handleWebViewMessage);
    return () => {
      window.removeEventListener('message', handleWebViewMessage);
      document.removeEventListener('message', handleWebViewMessage);
    };
  }, [handleWebViewMessage]);

  const uploadButton = (
    <button type="button">
      <PlusOutlined style={{ fontSize: 24 }} />
      <div style={{ marginTop: 8, fontSize: 14, fontWeight: 600 }}>Add Image</div>
    </button>
  );

  const handleCopyToClipboard = () => {
    if (!isMobile) {
      navigator.clipboard.writeText(`${asset?.latitude}, ${asset?.longitude}`);
      message.success('Coordinates copied to clipboard');
    } else {
    }
  };

  const getUploadComponent = () => {
    if (isMobile) {
      return (
        <div className="asset-photos-list-add-photo-button-container-mobile">
          <Button onClick={openCamera} icon={<CameraOutlined />} type="primary" size="large">
            Camera
          </Button>
          <Button onClick={openGallery} icon={<PictureOutlined />} type="primary" size="large">
            Gallery
          </Button>
        </div>
      );
    } else {
      return (
        <Upload
          name="avatar"
          listType="picture-card"
          className="asset-photos-list-add-photo-button"
          showUploadList={false}
          beforeUpload={beforeUpload}
          onChange={handleChange}
          customRequest={({ onSuccess }) => {
            // Dummy request to satisfy Upload component
            setTimeout(() => {
              onSuccess('ok');
            }, 0);
          }}
        >
          {loading ? <div>Loading...</div> : uploadButton}
        </Upload>
      );
    }
  };

  const getMainImage = () => {
    if (photosMeta.length > 0) {
      return getPhotoDisplayUrl(photosMeta[0]);
    }
    if (asset?.photos && asset.photos.length > 0) {
      return getPhotoDisplayUrl(asset.photos[0]);
    }
    return null;
  };

  const getDepth = useMemo(() => {
    if (asset?.depth) {
      return `${asset?.depth.toFixed(6)} m`;
    } else {
      return 'Unassigned';
    }
  }, [asset?.depth]);

  const getCoordinates = useMemo(() => {
    if (!asset?.isNew) {
      if (asset?.latitude && asset?.longitude) {
        return `${asset?.latitude.toFixed(6)}, ${asset?.longitude.toFixed(6)}`;
      } else {
        return 'Unassigned';
      }
    } else {
      if (ppkLoading) {
        return 'Loading...';
      } else {
        if (ppkPosition?.latitude && ppkPosition?.longitude) {
          const verticalSource = locationType?.vertical || 'Unknown';
          const horizontalSource = locationType?.horizontal || 'Unknown';

          return `${ppkPosition.latitude.toFixed(6)}, ${ppkPosition.longitude.toFixed(6)} (${verticalSource}/${horizontalSource})`;
        }
      }
    }
  }, [ppkPosition, ppkLoading, locationType, asset?.latitude, asset?.longitude, asset?.isNew]);

  const getAltitude = useMemo(() => {
    if (!asset?.isNew) {
      if (asset?.altitude) {
        return `${asset?.altitude.toFixed(6)} m`;
      } else {
        return 'Unassigned';
      }
    } else {
      if (ppkLoading) {
        return 'Loading...';
      } else {
        if (ppkPosition?.height || ppkPosition?.altitude) {
          const altitudeSource = locationType?.altitude || 'Unknown';
          const height = ppkPosition.height ?? ppkPosition.altitude;

          return `${height.toFixed(3)} m (${altitudeSource})`;
        }
      }
    }
  }, [asset?.altitude, ppkPosition, asset?.isNew, ppkLoading, locationType]);

  const assetOptions = useMemo(() => {
    const options = [
      ...allAssets.map((a) => ({
        label: a.name,
        value: a,
      })),
      { label: 'New Asset', value: null },
    ];
    return options;
  }, [allAssets]);

  const canSave = useMemo(() => {
    // Case 1: Modifying existing asset from plus menu
    if (selectedAsset && selectedAsset.id && asset?.isNew && !ppkLoading) return true;

    // Case 2: Creating new asset from plus menu
    // Disable if asset name is empty
    if ((selectedAsset === null || selectedAsset?.value === null) && asset?.isNew && !ppkLoading) {
      return assetName.trim().length > 0;
    }

    // Case 3: Modifying existing asset from asset list
    if (!asset?.isNew && asset?.id && !ppkLoading) return true;

    return false;
  }, [selectedAsset, asset?.isNew, asset?.id, assetName, ppkLoading]);

  const height = isMobile ? '100%' : undefined;
  const width = isMobile ? '100%' : undefined;

  return (
    <>
      {messageContextHolder}
      <Modal
        open={isOpen}
        okButtonProps={{ hidden: true }}
        cancelButtonProps={{ hidden: true }}
        onCancel={closeModal}
        footer={false}
        closeIcon={null}
        zIndex={1000}
        style={{
          position: 'relative',
          top: 0,
          padding: 0,
          margin: 0,
          maxWidth: isMobile ? '100vw' : undefined,
        }}
        styles={{
          body: {
            padding: 0,
            height: isMobile ? '100vh' : undefined,
          },
          content: {
            padding: 0,
            height: isMobile ? '100vh' : undefined,
            borderRadius: isMobile ? 0 : undefined,
          },
          mask: {
            // Optional: remove the backdrop on mobile for true fullscreen
            backgroundColor: isMobile ? 'transparent' : undefined,
          },
        }}
        height={height}
        width={width}
        wrapClassName={isMobile ? 'fullscreen-modal' : ''}
        keyboard
        destroyOnClose
        centered
      >
        <div
          className={isMobile ? 'asset-modal-wrapper-mobile' : ''}
          ref={assetModalRef}
          id="asset-modal-wrapper"
        >
          <div className="asset-modal-header">
            <ArrowLeftOutlined className="asset-modal-header-close" onClick={closeModal} />
            <div className="asset-modal-header-title">{asset?.name || 'Record Asset'}</div>
          </div>
          <div className="asset-modal-content">
            <div className="asset-modal-main-image">
              {getMainImage() ? (
                <Image
                  src={getMainImage()}
                  preview={{ src: getMainImage() }}
                  alt={asset?.name || 'New Asset Main Image'}
                  className="asset-modal-main-image"
                  placeholder={
                    <div
                      className="lazy-photo lazy-photo--loading"
                      style={{ width: '100%', height: '100%' }}
                    />
                  }
                />
              ) : (
                <div className="asset-modal-main-image-placeholder">
                  <div className="asset-modal-main-image-placeholder-text">Main Image</div>
                </div>
              )}
            </div>

            <div className="asset-info-card">
              <div className="asset-info-card-row">
                <div className="asset-info-card-row-label">Layer</div>
                <div className="asset-info-card-row-value">{asset?.layerName || 'Unassigned'}</div>
              </div>
              <div className="asset-info-card-row">
                <div className="asset-info-card-row-label">Depth</div>
                <div className="asset-info-card-row-value">{getDepth}</div>
              </div>
              <div className="asset-info-card-row">
                <div className="asset-info-card-row-label">Altitude</div>
                <div className="asset-info-card-row-value">{getAltitude}</div>
              </div>
              <div className="asset-info-card-row">
                <div className="asset-info-card-row-label">Coordinates</div>
                <div
                  className="asset-info-card-row-value clickable"
                  onClick={handleCopyToClipboard}
                >
                  {getCoordinates}
                </div>
              </div>
              {locationTimedOut ||
                (locationType !== 'PPK' && (
                  <div className="asset-info-card-row">
                    <div className="asset-info-card-row-label">Refresh PPK Position</div>
                    <ReloadOutlined
                      onClick={refreshPPKPosition}
                      spin={isSpinning}
                      style={{ cursor: 'pointer' }}
                      size={20}
                    />
                  </div>
                ))}
            </div>

            {asset?.isNew && (
              <div className="assigned-to-asset">
                <div className="assigned-to-asset-label">Assign to (Asset):</div>
                <Dropdown
                  options={assetOptions}
                  className="assigned-to-asset-dropdown"
                  value={selectedAsset}
                  onChange={(e) => {
                    setSelectedAsset(e.value);
                    log('Setting selected asset:');
                    log(e.value);
                    log('--------------------------------');
                  }}
                />
              </div>
            )}

            {(selectedAsset === null || selectedAsset?.value === null) && asset?.isNew && (
              <div className="asset-name">
                <div className="asset-name-title">Name* : </div>
                <Input
                  value={assetName}
                  onChange={(e) => setAssetName(e.target.value)}
                  placeholder="Enter asset name..."
                />
              </div>
            )}

            <div className="asset-notes">
              <div className="asset-notes-title">Notes:</div>
              <TextArea
                value={notes}
                placeholder="Enter notes here..."
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
                style={{
                  width: '100%',
                  height: '100px',
                  resize: 'none',
                }}
              />
            </div>

            <div className="asset-photos">
              <div className="asset-photos-title">Additional Images:</div>
              <div className="asset-photos-list">
                {photosMeta.slice(1).map((photo, idx) => {
                  const statusMeta = STATUS_STYLES[photo.status];
                  const actualIdx = idx + 1; // Account for slice(1)
                  return (
                    <div
                      key={photo.id || photo.tempId || actualIdx}
                      style={{ position: 'relative', display: 'inline-block' }}
                    >
                      {statusMeta && (
                        <Tag
                          color={statusMeta.color}
                          style={{ position: 'absolute', top: 8, left: 8, zIndex: 10 }}
                        >
                          {statusMeta.label}
                        </Tag>
                      )}
                      {photo.status === PHOTO_STATUS.ERROR && (
                        <Button
                          type="text"
                          size="small"
                          danger
                          onClick={() => retryPhotoUpload(photo.tempId)}
                          style={{ position: 'absolute', bottom: 8, left: 8, zIndex: 10 }}
                          disabled={submitting || isUpdatingAsset || isCreatingAsset}
                        >
                          Retry
                        </Button>
                      )}
                      <Image
                        src={getPhotoDisplayUrl(photo)}
                        alt={`${asset?.name} - Photo ${actualIdx + 1}`}
                        className="asset-photos-list-item"
                        preview
                        placeholder={
                          <LazyPhoto
                            photo={photo}
                            alt={`${asset?.name} - Photo ${actualIdx + 1}`}
                            objectFit="cover"
                          />
                        }
                      />
                      <PrimeButton
                        icon="pi pi-trash"
                        text
                        size="small"
                        severity="danger"
                        onClick={() => handleDeletePhoto(actualIdx)}
                        style={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          zIndex: 10,
                          backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        }}
                        disabled={
                          submitting ||
                          isUpdatingAsset ||
                          isCreatingAsset ||
                          photo.status === PHOTO_STATUS.DELETING ||
                          photo.status === PHOTO_STATUS.UPLOADING
                        }
                      />
                    </div>
                  );
                })}
                <div className="asset-photos-list-add-photo-button-container">
                  {getUploadComponent()}
                </div>
              </div>
            </div>
          </div>
          <div className="asset-modal-footer">
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSave}
              disabled={
                !canSave ||
                submitting ||
                isUpdatingAsset ||
                isCreatingAsset ||
                ppkLoading ||
                hasUploadingPhotos
              }
              loading={
                submitting || isUpdatingAsset || isCreatingAsset || ppkLoading || hasUploadingPhotos
              }
            >
              {hasUploadingPhotos
                ? `Uploading Photos (${photosMeta.filter((p) => p.status === PHOTO_STATUS.UPLOADING).length})`
                : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AssetView;
